/**
 * SnowballPlanner — Full Wave A through Wave E.
 *
 * Wave A — Foundation (P1, P2, P3, P4, P6)
 * Wave B — India Inputs (P5 CLP editor, P7 type-specific, P8 Subvention checker)
 * Wave C — Hero Output (P9 Sweet Spot + WhatsApp Story Card)
 * Wave D — Intelligence (P10 AI Coach, P11 Velocity, P12 A/B, P13 Stress)
 * Wave E — Onboarding (P14 Wizard)
 */
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Plus, Trash2, Building2, Building, TreePine, AlertTriangle,
  Info, ChevronDown, ChevronUp, BadgeCheck, ShieldAlert, Wand2,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, ReferenceLine, Area, AreaChart,
} from "recharts";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { inr } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

import CLPTrancheEditor from "./CLPTrancheEditor";
import StoryCard from "./StoryCard";
import VelocityGauge from "./VelocityGauge";
import AICoach from "./AICoach";
import StressTest from "./StressTest";
import ScenarioCompare from "./ScenarioCompare";
import SnowballSliders from "./SnowballSliders";
import SnowballWizard from "./SnowballWizard";
import EmailCalcButton from "../EmailCalcButton";

// ---------------------------------------------------------------------------
const newProperty = (idx = 0) => ({
  id: `p${Date.now()}-${idx}`,
  name: `Property ${idx + 1}`,
  type: "flat",
  price: 5000000,
  down_payment_pct: 20,
  loan_rate_pct: 8.75,
  loan_tenure_years: 20,
  monthly_rent: 25000,
  annual_rent_increase_pct: 5,
  annual_appreciation_pct: 7,
  is_under_construction: false,
  possession_month: 0,
  desired_buy_month: 0,
  rental_start_month: null,
  payment_plan: "lump_sum",
  clp_tranches: [],
  monthly_maintenance: 2000,
  property_tax_annual: 10000,
  vacancy_pct: 5,
  rent_tds_pct: 0,
});

const PROPERTY_ICONS = { flat: Building, plot: TreePine, commercial: Building2 };

// Apply P7 type-specific defaults when the user switches type.
const applyTypeDefaults = (prop, newType) => {
  if (newType === prop.type) return prop;
  const updated = { ...prop, type: newType };
  if (newType === "plot") {
    updated.monthly_rent = 0;
    updated.annual_appreciation_pct = 12;
    updated.rent_tds_pct = 0;
  } else if (newType === "commercial") {
    updated.loan_rate_pct = 10.0;
    updated.rent_tds_pct = 10;
    if (updated.monthly_rent === 0) {
      updated.monthly_rent = Math.round(prop.price * 0.0075);
    }
  } else {
    // flat
    updated.rent_tds_pct = 0;
    if (updated.monthly_rent === 0) {
      updated.monthly_rent = Math.round(prop.price * 0.005);
    }
  }
  return updated;
};

// ---------------------------------------------------------------------------
function NumField({ label, value, onChange, suffix = "", min, max, step = 1, testid, hint }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="relative">
        <Input
          type="number"
          inputMode="decimal"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          className="font-mono mt-1"
          data-testid={testid}
        />
        {suffix && <span className="absolute right-3 top-1/2 mt-0.5 text-xs text-muted-foreground">{suffix}</span>}
      </div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  const color = accent === "good" ? "text-emerald-700"
              : accent === "bad" ? "text-rose-700"
              : accent === "hero" ? "text-[#0B7B6E]"
              : "text-foreground";
  return (
    <div className="rounded-md border border-[#e5e1d6] bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-lg font-serif ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Property card — now type-aware (P7) and CLP-editor-aware (P5)
// ---------------------------------------------------------------------------
function PropertyCard({ idx, prop, onChange, onRemove, canRemove }) {
  const up = (k, v) => onChange({ ...prop, [k]: v });
  const Icon = PROPERTY_ICONS[prop.type] || Building;
  const [expanded, setExpanded] = useState(idx === 0);

  return (
    <div className="card-flat p-5 space-y-3" data-testid={`snowball-property-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <Icon size={18} className="text-[#0B7B6E]" />
          <Input
            value={prop.name}
            onChange={(e) => up("name", e.target.value)}
            className="font-serif text-base border-0 px-0 focus-visible:ring-0 shadow-none bg-transparent"
            data-testid={`snowball-name-${idx}`}
          />
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground hover:text-foreground" data-testid={`snowball-expand-${idx}`}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {canRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            data-testid={`snowball-remove-${idx}`}
            className="text-rose-600 hover:text-rose-700"
          >
            <Trash2 size={16} />
          </Button>
        )}
      </div>

      {/* Summary chip row when collapsed */}
      {!expanded && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{prop.type}</span>
          <span>•</span>
          <span>₹{(prop.price / 100000).toFixed(1)}L</span>
          <span>•</span>
          <span>DP {prop.down_payment_pct}%</span>
          {prop.type !== "plot" && <><span>•</span><span>₹{prop.monthly_rent.toLocaleString("en-IN")}/mo rent</span></>}
          {prop.is_under_construction && <><span>•</span><span>UC ({prop.possession_month}mo)</span></>}
          <span>•</span>
          <span>buy@M{prop.desired_buy_month}</span>
        </div>
      )}

      {expanded && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Type</Label>
              <select
                value={prop.type}
                onChange={(e) => onChange(applyTypeDefaults(prop, e.target.value))}
                className="w-full mt-1 border border-input rounded-md bg-white px-3 py-2 text-sm"
                data-testid={`snowball-type-${idx}`}
              >
                <option value="flat">Flat / Apartment</option>
                <option value="plot">Plot (no rent)</option>
                <option value="commercial">Commercial (10% TDS)</option>
              </select>
            </div>
            <NumField label="Price (₹)" value={prop.price} onChange={(v) => up("price", v)} testid={`snowball-price-${idx}`} />
            <NumField label="Down payment %" value={prop.down_payment_pct} onChange={(v) => up("down_payment_pct", v)} suffix="%" min={5} max={100} />
            <NumField label="Loan rate %" value={prop.loan_rate_pct} onChange={(v) => up("loan_rate_pct", v)} suffix="%" min={4} max={20} step={0.05} />
            <NumField label="Loan tenure (years)" value={prop.loan_tenure_years} onChange={(v) => up("loan_tenure_years", v)} min={5} max={30} />
            <NumField label="Desired buy month" value={prop.desired_buy_month} onChange={(v) => up("desired_buy_month", v)} min={0} max={360} testid={`snowball-buy-month-${idx}`} />

            {/* Rent fields — hidden for plots (P7) */}
            {prop.type !== "plot" && (
              <>
                <NumField label="Monthly rent (₹)" value={prop.monthly_rent} onChange={(v) => up("monthly_rent", v)} testid={`snowball-rent-${idx}`} />
                <NumField label="Rent ↑ / year %" value={prop.annual_rent_increase_pct} onChange={(v) => up("annual_rent_increase_pct", v)} suffix="%" step={0.5} />
              </>
            )}
            <NumField label="Appreciation %" value={prop.annual_appreciation_pct} onChange={(v) => up("annual_appreciation_pct", v)} suffix="%" step={0.5} />

            {/* TDS only for commercial */}
            {prop.type === "commercial" && (
              <NumField
                label="Rent TDS %"
                value={prop.rent_tds_pct}
                onChange={(v) => up("rent_tds_pct", v)}
                suffix="%"
                hint="Indian commercial rent attracts 10% TDS"
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-[#e5e1d6] pt-3">
            <div>
              <Label className="text-xs text-muted-foreground">Payment plan</Label>
              <select
                value={prop.payment_plan}
                onChange={(e) => up("payment_plan", e.target.value)}
                className="w-full mt-1 border border-input rounded-md bg-white px-3 py-2 text-sm"
                data-testid={`snowball-plan-${idx}`}
              >
                <option value="lump_sum">Lump-sum (full DP, full EMI)</option>
                <option value="clp">CLP (tranche, pre-EMI)</option>
                <option value="subvention">Subvention (no EMI till possession)</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm py-2">
                <input
                  type="checkbox"
                  checked={prop.is_under_construction}
                  onChange={(e) => up("is_under_construction", e.target.checked)}
                  className="rounded"
                  data-testid={`snowball-uc-${idx}`}
                />
                Under construction
              </label>
            </div>
            {prop.is_under_construction && (
              <NumField
                label="Possession in (months)"
                value={prop.possession_month}
                onChange={(v) => up("possession_month", v)}
                min={1}
                max={60}
                testid={`snowball-possession-${idx}`}
              />
            )}
          </div>

          {/* CLP Tranche editor (P5) — only when plan is CLP */}
          {prop.payment_plan === "clp" && (
            <CLPTrancheEditor
              tranches={prop.clp_tranches}
              onChange={(t) => up("clp_tranches", t)}
              possessionMonth={prop.possession_month}
              dataTestid={`clp-editor-${idx}`}
            />
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-[#e5e1d6] pt-3">
            <NumField label="Maintenance / mo (₹)" value={prop.monthly_maintenance} onChange={(v) => up("monthly_maintenance", v)} />
            <NumField label="Property tax / year" value={prop.property_tax_annual} onChange={(v) => up("property_tax_annual", v)} />
            {prop.type !== "plot" && (
              <NumField label="Vacancy %" value={prop.vacancy_pct} onChange={(v) => up("vacancy_pct", v)} suffix="%" />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function SnowballPlanner() {
  const [globals, setGlobals] = useState({
    horizon_months: 240,
    starting_snowball_fund: 1500000,
    monthly_salary_surplus: 80000,
    annual_salary_growth_pct: 6,
    snowball_fund_yield_pct: 4,
  });

  const [properties, setProperties] = useState([
    { ...newProperty(0), name: "Flat 1 — RTM Pune", desired_buy_month: 0 },
    {
      ...newProperty(1),
      name: "Flat 2 — UC Hinjewadi",
      price: 8500000,
      monthly_rent: 35000,
      is_under_construction: true,
      possession_month: 30,
      desired_buy_month: 6,
      payment_plan: "clp",
    },
    {
      ...newProperty(2),
      name: "Flat 3 — RTM Mumbai",
      price: 15000000,
      monthly_rent: 65000,
      desired_buy_month: 36,
    },
  ]);

  const [baseRes, setBaseRes] = useState(null);
  const [overlayRes, setOverlayRes] = useState(null);     // live slider result
  const [slidersBusy, setSlidersBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const [activeView, setActiveView] = useState("plan");   // "plan" | "compare" | "stress"
  const [showWizard, setShowWizard] = useState(false);

  const upGlobal = (k, v) => setGlobals({ ...globals, [k]: v });
  const upProperty = (i, p) => setProperties(properties.map((x, j) => (i === j ? p : x)));
  const addProperty = () => setProperties([...properties, newProperty(properties.length)]);
  const removeProperty = (i) => setProperties(properties.filter((_, j) => j !== i));

  // Built request — what's sent to /snowball/simulate
  // Memoised based on actual input state (not the function ref) so that
  // child components like SnowballSliders don't receive a new object ref
  // on every render. Inline buildRequest() in JSX would cause infinite
  // re-render cascades through recharts subscriptions.
  const builtRequest = useMemo(() => ({
    ...globals,
    properties: properties.map((p) => ({
      ...p,
      rental_start_month: p.rental_start_month ?? null,
      clp_tranches: p.payment_plan === "clp" ? p.clp_tranches : [],
    })),
  }), [globals, properties]);

  const run = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/snowball/simulate", builtRequest);
      setBaseRes(data);
      setOverlayRes(null);
      toast.success("Simulation complete");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Simulation failed");
    } finally {
      setBusy(false);
    }
  }, [builtRequest]);

  // Wizard returns a complete request — set state then auto-run.
  const handleWizardComplete = useCallback((req) => {
    setGlobals({
      horizon_months: req.horizon_months,
      starting_snowball_fund: req.starting_snowball_fund,
      monthly_salary_surplus: req.monthly_salary_surplus,
      annual_salary_growth_pct: req.annual_salary_growth_pct,
      snowball_fund_yield_pct: req.snowball_fund_yield_pct,
    });
    setProperties(req.properties);
    setShowWizard(false);
    setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/snowball/simulate", req);
        setBaseRes(data);
        setOverlayRes(null);
        toast.success("Wizard simulation ready");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Simulation failed");
      } finally {
        setBusy(false);
      }
    }, 100);
  }, []);

  // The "current" result — overlay (slider) if present, else baseline.
  const view = overlayRes || baseRes;

  // Chart data — sample to keep render snappy at 360-month horizons
  const chartData = useMemo(() => {
    if (!view) return [];
    const step = Math.max(1, Math.floor(view.monthly.length / 80));
    return view.monthly.filter((_, i) => i % step === 0).map((r) => ({
      month: r.month, fund: r.fund, cf: r.portfolio_cf,
    }));
  }, [view]);

  // Wizard takeover
  if (showWizard) {
    return (
      <SnowballWizard
        onComplete={handleWizardComplete}
        onCancel={() => setShowWizard(false)}
      />
    );
  }

  return (
    <div data-testid="snowball-planner" className="space-y-6">
      {/* Hero ------------------------------------------------------- */}
      <div className="card-flat p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wand2 size={20} className="text-[#C85A32]" />
              <span className="eyebrow">Rental Snowball Planner</span>
            </div>
            <h2 className="font-serif text-3xl mb-2">
              One Fund. Many properties. Compound to cashflow positive.
            </h2>
            <p className="text-sm text-muted-foreground max-w-2xl">
              The Snowball Fund collects salary surplus + portfolio cashflow.
              When it has enough for the next down payment, the next property buys itself.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowWizard(true)}
            data-testid="open-wizard"
            className="self-start"
          >
            <Wand2 size={14} className="mr-1" /> Use Quick Wizard
          </Button>
        </div>
      </div>

      {/* View switcher ---------------------------------------------- */}
      <div className="flex items-center gap-1 border-b border-[#e5e1d6] pb-0">
        {[
          { id: "plan", label: "Plan" },
          { id: "compare", label: "Compare A vs B" },
          { id: "stress", label: "Stress test" },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => setActiveView(v.id)}
            className={`px-4 py-2 text-sm transition border-b-2 -mb-px ${
              activeView === v.id
                ? "border-[#C85A32] text-foreground font-medium"
                : "border-transparent text-muted-foreground"
            }`}
            data-testid={`snowball-view-${v.id}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* === PLAN view ============================================== */}
      {activeView === "plan" && (
        <>
          {/* Globals */}
          <div className="card-flat p-5">
            <h3 className="font-serif text-xl mb-3">Your inputs</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <NumField label="Horizon (months)" value={globals.horizon_months} onChange={(v) => upGlobal("horizon_months", v)} min={12} max={360} testid="snowball-horizon" />
              <NumField label="Starting fund (₹)" value={globals.starting_snowball_fund} onChange={(v) => upGlobal("starting_snowball_fund", v)} testid="snowball-start-fund" />
              <NumField label="Salary surplus / mo (₹)" value={globals.monthly_salary_surplus} onChange={(v) => upGlobal("monthly_salary_surplus", v)} testid="snowball-salary" />
              <NumField label="Salary ↑ / year %" value={globals.annual_salary_growth_pct} onChange={(v) => upGlobal("annual_salary_growth_pct", v)} suffix="%" step={0.5} />
              <NumField label="Fund yield %" value={globals.snowball_fund_yield_pct} onChange={(v) => upGlobal("snowball_fund_yield_pct", v)} suffix="%" step={0.5} />
            </div>
          </div>

          {/* Properties */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl">Your target portfolio ({properties.length})</h3>
              <Button onClick={addProperty} variant="outline" data-testid="snowball-add-property">
                <Plus size={16} className="mr-1" /> Add property
              </Button>
            </div>
            {properties.map((p, i) => (
              <PropertyCard
                key={p.id}
                idx={i}
                prop={p}
                onChange={(x) => upProperty(i, x)}
                onRemove={() => removeProperty(i)}
                canRemove={properties.length > 1}
              />
            ))}
          </div>

          {/* Run */}
          <div className="text-center">
            <Button onClick={run} disabled={busy} size="lg" className="font-serif" data-testid="snowball-run">
              {busy ? "Simulating…" : "Run simulation →"}
            </Button>
          </div>

          {/* Results */}
          {view && (
            <div className="space-y-5" data-testid="snowball-results">
              {/* P9 — Hero Story Card */}
              <StoryCard result={view} />

              {/* Warnings */}
              {view.warnings?.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-center gap-2 mb-1 text-amber-800">
                    <AlertTriangle size={14} />
                    <span className="text-sm font-medium">Heads-up</span>
                  </div>
                  <ul className="list-disc pl-6 text-xs text-amber-700 space-y-0.5">
                    {view.warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Live slider with overlay indicator */}
              <SnowballSliders
                baseRequest={baseRes ? builtRequest : null}
                onResult={setOverlayRes}
                onBusy={setSlidersBusy}
              />
              {overlayRes && (
                <div className="text-xs text-muted-foreground text-center">
                  {slidersBusy ? "⏳ Updating live what-if…" : "✨ Showing live what-if results (sliders moved)"}
                </div>
              )}

              {/* Velocity Score + Subvention check side by side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <VelocityGauge velocity={view.velocity_score} />
                {/* P8 — Subvention checker */}
                {view.subvention_checks?.length > 0 ? (
                  <SubventionCheckCard checks={view.subvention_checks} />
                ) : (
                  <div className="card-flat p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <BadgeCheck size={16} className="text-[#0B7B6E]" />
                      <h4 className="font-serif text-lg">Hero stats</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Stat label="Sweet Spot" value={view.sweet_spot_month != null ? `M${view.sweet_spot_month}` : "—"} accent="hero" />
                      <Stat label="Year-end wealth" value={inr(view.year20_net_wealth)} accent="hero" />
                      <Stat label="Properties bought" value={view.per_property.filter((p) => p.purchased).length} />
                      <Stat label="Self-invested" value={inr(view.totals.total_self_invested)} />
                    </div>
                  </div>
                )}
              </div>

              {/* Snowball Fund chart */}
              <div className="card-flat p-5">
                <h4 className="font-serif text-lg mb-3">Snowball Fund + Portfolio Cashflow</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="month" tickFormatter={(m) => `M${m}`} />
                    <YAxis tickFormatter={(v) => v >= 10000000 ? `₹${(v / 10000000).toFixed(1)}cr` : `₹${(v / 100000).toFixed(0)}L`} />
                    <Tooltip formatter={(v) => inr(v)} labelFormatter={(m) => `Month ${m}`} />
                    <Legend />
                    <ReferenceLine y={0} stroke="#999" strokeDasharray="3 3" />
                    {view.sweet_spot_month !== null && (
                      <ReferenceLine
                        x={view.sweet_spot_month}
                        stroke="#0B7B6E"
                        strokeDasharray="4 4"
                        label={{ value: "Sweet spot", position: "top", fill: "#0B7B6E", fontSize: 11 }}
                      />
                    )}
                    <Area type="monotone" dataKey="fund" name="Snowball Fund" stroke="#0B7B6E" fill="#0B7B6E" fillOpacity={0.15} />
                    <Area type="monotone" dataKey="cf" name="Portfolio CF" stroke="#C85A32" fill="#C85A32" fillOpacity={0.1} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* P10 AI Coach */}
              <AICoach request={builtRequest} result={view} />

              {/* Purchases */}
              {view.purchases_log?.length > 0 && (
                <div className="card-flat p-5">
                  <h4 className="font-serif text-lg mb-3">Purchase Timeline</h4>
                  <div className="space-y-2">
                    {view.purchases_log.map((p) => (
                      <div
                        key={p.property_id + p.month}
                        className="flex flex-wrap items-center gap-3 text-sm py-2 border-b border-[#e5e1d6] last:border-0"
                        data-testid={`snowball-purchase-${p.property_id}`}
                      >
                        <span className="font-mono px-2 py-0.5 bg-[#0B7B6E]/10 text-[#0B7B6E] rounded text-xs">M{p.month}</span>
                        <span className="font-medium">{p.name}</span>
                        <span className="text-muted-foreground">DP {inr(p.down_payment)}</span>
                        <span className="text-muted-foreground">Loan {inr(p.loan)}</span>
                        <span className="ml-auto text-xs text-muted-foreground">Fund left: {inr(p.fund_remaining)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-property breakdown */}
              <div className="card-flat p-5 overflow-x-auto">
                <h4 className="font-serif text-lg mb-3">Per-property breakdown</h4>
                <table className="w-full text-sm" data-testid="snowball-per-property-table">
                  <thead className="text-xs uppercase text-muted-foreground border-b border-[#e5e1d6]">
                    <tr>
                      <th className="text-left p-2">Property</th>
                      <th className="text-right p-2">Bought</th>
                      <th className="text-right p-2">EMI</th>
                      <th className="text-right p-2">Rent start</th>
                      <th className="text-right p-2">Total EMI</th>
                      <th className="text-right p-2">Total Rent</th>
                      <th className="text-right p-2">Terminal Value</th>
                      <th className="text-right p-2">Terminal Equity</th>
                      <th className="text-right p-2">XIRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.per_property.map((p) => (
                      <tr key={p.id} className="border-b border-[#e5e1d6] last:border-0">
                        <td className="p-2 font-medium">{p.name}</td>
                        {p.purchased ? (
                          <>
                            <td className="p-2 text-right font-mono">M{p.purchased_month}</td>
                            <td className="p-2 text-right font-mono">{inr(p.standard_emi)}</td>
                            <td className="p-2 text-right font-mono">M{p.rental_start_month}</td>
                            <td className="p-2 text-right font-mono">{inr(p.total_emi_paid)}</td>
                            <td className="p-2 text-right font-mono text-emerald-700">{inr(p.total_rent_received)}</td>
                            <td className="p-2 text-right font-mono">{inr(p.terminal_market_value)}</td>
                            <td className="p-2 text-right font-mono font-semibold">{inr(p.terminal_equity)}</td>
                            <td className="p-2 text-right font-mono text-[#0B7B6E] font-semibold">
                              {p.equity_xirr_pct != null ? `${p.equity_xirr_pct}%` : "—"}
                            </td>
                          </>
                        ) : (
                          <td colSpan={8} className="p-2 text-rose-700 text-right">
                            Never purchased — {p.warning || "fund insufficient"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-[10px] text-muted-foreground italic">
                Results are indicative estimates based on inputs. Tax, brokerage and
                registration costs are excluded from this Wave A model. Consult a CA before
                investment decisions.
              </div>

              {/* Save → Email PDF */}
              <div className="flex justify-center pt-2">
                <EmailCalcButton
                  calcType="snowball"
                  title="My Rental Snowball Plan"
                  request={builtRequest}
                  result={view}
                  buttonLabel="Email me this Snowball Plan (PDF)"
                  dataTestid="snowball-email-btn"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* === COMPARE view =========================================== */}
      {activeView === "compare" && (
        <ScenarioCompare baseRequest={baseRes ? builtRequest : null} />
      )}

      {/* === STRESS view ============================================ */}
      {activeView === "stress" && (
        <StressTest request={baseRes ? builtRequest : null} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// P8 — Subvention Cost Checker
// ---------------------------------------------------------------------------
function SubventionCheckCard({ checks }) {
  const verdictColor = (v) =>
    v === "green" ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", label: "Subvention wins" } :
    v === "amber" ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", label: "Marginal" } :
    { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", label: "Expensive" };
  return (
    <div className="card-flat p-5" data-testid="subvention-check-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert size={16} className="text-[#0B7B6E]" />
        <h4 className="font-serif text-lg">Subvention vs CLP cost check</h4>
      </div>
      <div className="space-y-2">
        {checks.map((c) => {
          const col = verdictColor(c.verdict);
          return (
            <div key={c.id} className={`rounded-md border ${col.border} ${col.bg} p-3`} data-testid={`subvention-row-${c.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm">{c.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full bg-white border ${col.border} ${col.text} font-medium`}>
                  {col.label}
                </span>
              </div>
              <div className="text-xs space-y-0.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subvention total outflow</span>
                  <span className="font-mono">{inr(c.subvention_total_outflow)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CLP counterfactual</span>
                  <span className="font-mono">{inr(c.clp_counterfactual_outflow)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={`font-medium ${col.text}`}>Δ extra cost</span>
                  <span className={`font-mono font-semibold ${col.text}`}>
                    {c.delta_extra_cost >= 0 ? "+" : ""}{inr(c.delta_extra_cost)}
                  </span>
                </div>
              </div>
              <div className="text-xs text-foreground mt-2">{c.advice}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
